import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { MaterialReactTable } from 'material-react-table';
import Select from 'react-select';
import {
  Box, Button, createTheme, ThemeProvider,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Grid,
} from '@mui/material';
import { Attachment as AttachmentIcon, BuildCircle as BuildCircleIcon } from '@mui/icons-material';
import { readableStamp } from '../../../../utils/dateFunctions';
import { updateMpiWorkItem, addMpiWorkItem } from '../../../../actions/apiMpiWorkItems';
import { addWorkItemPartsBulk } from '../../../../actions/apiWorkItemParts';
import { getCatalogParts } from '../../../../actions/apiGetInfo';
import { lookupUserById } from '../../../../actions/lookups';
import VehiclePartsSection from '../../../../components/Parts/VehiclePartsSection';
import MpiWorkItemAttachments from '../../../../components/Parts/MpiWorkItemAttachments';
import PartsSection from './MpiFormBuilder/components/PartsSection';
import useFormInstructions from './MpiFormBuilder/hooks/useFormInstructions';
import { tabWorkItemPartsEnabled, presetPartsToItems, partsForMpiWorkItem } from '../../../../components/Parts/partsUtils';

const APPROVED_STATES = ['approved', 'completed'];
const num = v => Number(v) || 0;
const money = v => (Number(v) || 0).toFixed(2);
const isFlagged = v => v === true || v === 'true';

const DEFAULT_HEADERS = [
  { field: 'category', label: 'Classification' },
  { field: 'name', label: 'Work Item' },
  { field: 'description', label: 'Description' },
  { field: 'parts', label: 'Parts' },
  { field: 'labor_hours', label: 'Labor Units' },
  { field: 'labor_rate', label: 'Labor Rate' },
  { field: 'labor_total', label: 'Labor Total' },
  { field: 'estimate_total', label: 'Total' },
  { field: 'ro_status', label: 'Status' },
];

const WorkItemsForm = ({ tab }) => {
  const dispatch = useDispatch();
  const activeVehicle = useSelector(state => state.settings_refresh.active_vehicle);
  const all_tabs = useSelector(state => state.settings_site?.all_tabs || []);
  const dealerSettings = useSelector(state => state.settings_site.dealer_settings);
  const installation = useSelector(state => state.settings_dealerships.installation);
  const currentUser = useSelector(state => state.current_user);
  const currentUserProfile = useSelector(state => state.settings_users.current_user_profile);
  const workItemPresets = useSelector(state => state.settings_refresh.work_item_presets || []);
  const catalog_parts = useSelector(state => state.settings_refresh?.catalog_parts || []);
  const currentDealership = useSelector(state => state.settings_dealerships.current_dealership);

  const formInstructions = useFormInstructions(tab);
  const workItemPartsEnabled = tabWorkItemPartsEnabled(all_tabs, tab);
  const uid = parseInt(currentUser?.user, 10) || currentUserProfile?.id || null;

  const [confirmingTask, setConfirmingTask] = useState(null);
  const [pullOpen, setPullOpen] = useState(false);
  const [pullSelected, setPullSelected] = useState({});
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [selectedPresets, setSelectedPresets] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [isDisabled, setIsDisabled] = useState(true);
  const [canUndo, setCanUndo] = useState(false);
  const [cantApprove, setCantApprove] = useState(true);
  useEffect(() => {
    if (!currentUser) {
      setIsDisabled(true);
      return;
    }
    const hasApprove = currentUser.approve_estimates || currentUserProfile.work_item_approve;
    const hasEdit = currentUserProfile.work_item_edit;
    const isAdmin = ['admin', 'super_admin'].includes(currentUser.position);
    setIsDisabled(!(hasApprove || isAdmin || hasEdit));
    setCanUndo(!!(hasApprove || isAdmin));
    setCantApprove(!(hasApprove || isAdmin));
  }, [currentUser, currentUserProfile]);

  useEffect(() => {
    if (activeVehicle?.id) dispatch(getCatalogParts());
  }, [activeVehicle?.id]);

  const defaultLaborRate = useMemo(() => {
    const s = _.find(dealerSettings, o => o.key === 'default_labor_rate' && o.dealership === installation?.subdomain);
    return num(s?.value) || 100;
  }, [dealerSettings, installation]);

  const visibleWorkItemPresets = useMemo(() => {
    if (!currentDealership) return workItemPresets;
    return workItemPresets.filter(p => {
      const d = (p.dealership || '').trim();
      if (!d) return true;
      return d.split(',').map(s => s.trim()).includes(currentDealership);
    });
  }, [workItemPresets, currentDealership]);

  const workItemOptions = useMemo(() => _.sortBy(visibleWorkItemPresets, 'name').map(o => ({
    id: o.id,
    label: o.name,
    value: o.name,
    parts_price: o.parts_price,
    labor_units: o.labor_units,
    labor_price: num(o.labor_price) > 0 ? o.labor_price : defaultLaborRate,
    category: o.category,
    estimate_type: o.estimate_type,
    description: o.description,
  })), [visibleWorkItemPresets, defaultLaborRate]);

  const presetData = useMemo(() => _.sortBy(visibleWorkItemPresets, 'name')
    .filter(p => (selectedCategory === 'all' ? true : p.category === selectedCategory))
    .map(p => ({
      id: p.id,
      name: p.name,
      category: p.category,
      parts_price: p.parts_price,
      labor_units: p.labor_units,
      labor_price: p.labor_price,
      estimate_type: p.estimate_type,
      description: p.description,
    })), [visibleWorkItemPresets, selectedCategory]);

  const allItems = useMemo(() => activeVehicle?.mpi_work_items || [], [activeVehicle]);

  const rows = useMemo(() => {
    const included = allItems.filter(i => !i.deleted
      && (APPROVED_STATES.includes(i.ro_status) || isFlagged(i.include_in_work_items)));
    return _.orderBy(included, 'id', 'desc');
  }, [allItems]);

  const pullCandidates = useMemo(() => {
    const shown = new Set(rows.map(r => r.id));
    return _.orderBy(allItems.filter(i => !i.deleted && !shown.has(i.id)), 'id', 'desc');
  }, [allItems, rows]);

  const totals = useMemo(() => {
    let parts = 0;
    let labor = 0;
    let total = 0;
    let approved = 0;
    rows.forEach(r => {
      if (r.ro_status === 'declined') return;
      const laborTotal = num(r.labor_hours) * num(r.labor_rate);
      const partsTotal = num(r.parts_total) || num(r.parts) * (num(r.quantity) || 1);
      const est = num(r.estimate_total) || (partsTotal + laborTotal);
      parts += partsTotal;
      labor += laborTotal;
      total += est;
      if (APPROVED_STATES.includes(r.ro_status)) approved += est;
    });
    return {
      parts, labor, total, approved,
    };
  }, [rows]);

  const patch = (id, changes) => dispatch(updateMpiWorkItem(id, { vehicle_id: activeVehicle.id, ...changes }));

  const recalc = (row, overrides) => {
    const parts = overrides.parts != null ? num(overrides.parts) : num(row.parts);
    const quantity = (overrides.quantity != null ? num(overrides.quantity) : num(row.quantity)) || 1;
    const laborHours = overrides.labor_hours != null ? num(overrides.labor_hours) : num(row.labor_hours);
    const laborRate = overrides.labor_rate != null ? num(overrides.labor_rate) : num(row.labor_rate);
    const parts_total = parts * quantity;
    const labor_total = laborHours * laborRate;
    return { parts_total, labor_total, estimate_total: parts_total + labor_total };
  };

  const nowIso = () => new Date().toISOString();
  const userName = uidVal => (uidVal ? (dispatch(lookupUserById(uidVal))?.user_name || '') : '');

  const approve = item => () => patch(item.id, {
    ro_status: 'approved', approved_by_uid: uid, approved_by_date: nowIso(), declined_by_uid: null, declined_by_date: null, include_in_work_items: true,
  });
  const deny = item => () => patch(item.id, {
    ro_status: 'declined', declined_by_uid: uid, declined_by_date: nowIso(), include_in_work_items: true,
  });
  const complete = item => () => patch(item.id, { ro_status: 'completed', include_in_work_items: true });
  const undo = item => () => patch(item.id, {
    ro_status: item.ro_status === 'completed' ? 'approved' : 'pending',
    approved_by_uid: item.ro_status === 'completed' ? item.approved_by_uid : null,
    approved_by_date: item.ro_status === 'completed' ? item.approved_by_date : null,
    declined_by_uid: null,
    declined_by_date: null,
    include_in_work_items: true,
  });
  const removeFromWorkItems = item => () => patch(item.id, { include_in_work_items: false });

  const addLine = () => dispatch(addMpiWorkItem({
    vehicle_id: activeVehicle.id,
    include_in_work_items: true,
    ro_status: 'pending',
    category: 'mechanical',
    name: '',
    description: '',
    parts: 0,
    parts_total: 0,
    quantity: 1,
    labor_hours: 0,
    labor_rate: defaultLaborRate,
    labor_total: 0,
    estimate_total: 0,
  }, 'addWorkItemError'));

  const confirmPull = () => {
    Object.keys(pullSelected)
      .filter(id => pullSelected[id])
      .forEach(id => patch(parseInt(id, 10), { include_in_work_items: true }));
    setPullSelected({});
    setPullOpen(false);
  };

  const runBulk = (task, table) => {
    const action = task === 'Approve' ? approve : deny;
    table.getSelectedRowModel().rows.forEach(r => action(r.original)());
    table.resetRowSelection();
    setConfirmingTask(null);
  };

  const seedPresetParts = (presetId, mpiId) => {
    const fullPreset = workItemPresets.find(p => String(p.id) === String(presetId));
    if (!fullPreset) return;
    const basePayload = {
      vehicle_id: activeVehicle.id, work_item_source: 'new_work_items', mpi_work_item_id: mpiId, dealership: installation.subdomain,
    };
    const items = presetPartsToItems(fullPreset, catalog_parts, basePayload, uid);
    if (items.length) dispatch(addWorkItemPartsBulk(items, activeVehicle.id));
  };

  const onChangeWorkItem = id => e => {
    const laborRate = num(e.labor_price) > 0 ? num(e.labor_price) : defaultLaborRate;
    const laborTotal = num(e.labor_units) * laborRate;
    const parts = num(e.parts_price);
    dispatch(updateMpiWorkItem(id, {
      vehicle_id: activeVehicle.id,
      name: e.value,
      description: e.description,
      category: e.category,
      parts,
      parts_total: parts,
      quantity: 1,
      labor_hours: e.labor_units,
      labor_rate: laborRate,
      labor_total: laborTotal,
      estimate_total: parts + laborTotal,
      estimate_type: e.estimate_type,
      work_item_preset_id: e.id,
      include_in_work_items: true,
    }));
    seedPresetParts(e.id, id);
  };

  const onClickMultiple = () => {
    const selectedRows = Object.keys(selectedPresets)
      .filter(i => selectedPresets[i])
      .map(i => presetData[parseInt(i, 10)]);
    if (!selectedRows.length) {
      setFlyoutOpen(false);
      return;
    }
    const createOne = preset => {
      const laborRate = num(preset.labor_price) > 0 ? num(preset.labor_price) : defaultLaborRate;
      const laborTotal = num(preset.labor_units) * laborRate;
      const parts = num(preset.parts_price);
      return dispatch(addMpiWorkItem({
        vehicle_id: activeVehicle.id,
        include_in_work_items: true,
        ro_status: 'pending',
        name: preset.name,
        description: preset.description || '',
        category: preset.category,
        parts,
        parts_total: parts,
        quantity: 1,
        labor_hours: preset.labor_units || 0,
        labor_rate: laborRate,
        labor_total: laborTotal,
        estimate_total: parts + laborTotal,
        estimate_type: preset.estimate_type,
        work_item_preset_id: preset.id,
        layout_type: 'default',
        active: true,
        deleted: false,
        dealership: installation.subdomain,
      })).then(newId => ({ preset, newId }));
    };
    Promise.all(selectedRows.map(createOne)).then(created => {
      const items = [];
      created.forEach(({ preset, newId }) => {
        if (!newId) return;
        const fullPreset = workItemPresets.find(p => String(p.id) === String(preset.id));
        const basePayload = {
          vehicle_id: activeVehicle.id, work_item_source: 'new_work_items', mpi_work_item_id: newId, dealership: installation.subdomain,
        };
        items.push(...presetPartsToItems(fullPreset, catalog_parts, basePayload, uid));
      });
      if (items.length) dispatch(addWorkItemPartsBulk(items, activeVehicle.id));
    });
    setSelectedPresets({});
    setFlyoutOpen(false);
  };

  const lockRow = item => APPROVED_STATES.includes(item.ro_status);
  const editDisabled = row => lockRow(row.original) || isDisabled;

  const statusCell = item => {
    switch (item.ro_status) {
      case 'approved':
      case 'completed':
        return (
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start',
          }}
          >
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button className="btn btn-sm btn-primary text-capitalize" style={{ maxWidth: '220px' }} title={userName(item.approved_by_uid)}>
                Approved {userName(item.approved_by_uid)} {item.approved_by_date ? readableStamp(item.approved_by_date) : ''}
              </button>
              {canUndo && <button className="btn btn-sm btn-warning" onClick={undo(item)}>Undo</button>}
            </Box>
            {item.ro_status === 'approved' ? (
              <button className="btn btn-sm btn-success" onClick={complete(item)} disabled={cantApprove}>Complete</button>
            ) : (
              <button className="btn btn-sm btn-success" disabled>Completed</button>
            )}
          </Box>
        );
      case 'declined':
        return (
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button className="btn btn-sm btn-danger" title={userName(item.declined_by_uid)}>
              Denied {userName(item.declined_by_uid)} {item.declined_by_date ? readableStamp(item.declined_by_date) : ''}
            </button>
            {canUndo && <button className="btn btn-sm btn-warning space-left-1" onClick={undo(item)}>Undo</button>}
          </Box>
        );
      default:
        return (
          <div className="btn-group" role="group">
            <button className="btn btn-sm btn-primary" onClick={approve(item)} disabled={cantApprove}>Approve</button>
            <button className="btn btn-sm btn-danger" onClick={deny(item)} disabled={cantApprove}>Deny</button>
          </div>
        );
    }
  };

  const buildColumn = (h) => {
    const { field, label, inputtype } = h;
    switch (field) {
      case 'category':
        return {
          accessorKey: 'category',
          header: label,
          enableEditing: false,
          size: 110,
          accessorFn: row => row.category,
          Cell: ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {activeVehicle?.attachments?.some(a => a.mpi_work_item_id === row.original.id) && (
                <AttachmentIcon sx={{ fontSize: '1rem', transform: 'rotate(90deg)', color: 'green' }} />
              )}
              {partsForMpiWorkItem(activeVehicle, row.original.id).length > 0 && (
                <BuildCircleIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
              )}
              {row.original.category || 'N/A'}
            </Box>
          ),
        };
      case 'name':
        return {
          accessorKey: 'name',
          header: label,
          size: 180,
          enableEditing: true,
          accessorFn: row => row.name,
          muiEditTextFieldProps: ({ row }) => ({
            disabled: editDisabled(row),
            onBlur: e => patch(row.original.id, { name: e.target.value }),
          }),
          Cell: ({ row }) => (row.original.estimate_type === 'blank_parts_and_labor' ? (
            row.original.name || 'N/A'
          ) : (
            <Select
              options={workItemOptions}
              value={workItemOptions.filter(o => o.value === row.original.name)}
              onChange={onChangeWorkItem(row.original.id)}
              isDisabled={editDisabled(row)}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              className="text-capitalize"
              placeholder="Choose…"
              styles={{
                container: base => ({ ...base, width: '100%' }),
                menuPortal: base => ({ ...base, zIndex: 9999 }),
              }}
            />
          )),
        };
      case 'description':
        return {
          accessorKey: 'description',
          header: label,
          size: 200,
          enableEditing: true,
          accessorFn: row => row.description,
          muiEditTextFieldProps: ({ row }) => ({
            disabled: editDisabled(row),
            multiline: true,
            onBlur: e => patch(row.original.id, { description: e.target.value }),
          }),
          Cell: ({ row }) => (
            <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word', minWidth: '180px' }}>{row.original.description || 'N/A'}</Box>
          ),
        };
      case 'parts':
        return {
          accessorKey: 'parts',
          header: label,
          size: 60,
          enableEditing: true,
          accessorFn: row => (num(row.parts_total) || num(row.parts)),
          muiEditTextFieldProps: ({ row }) => ({
            disabled: editDisabled(row),
            type: 'number',
            onBlur: e => patch(row.original.id, { parts: num(e.target.value), ...recalc(row.original, { parts: e.target.value }) }),
          }),
          Cell: ({ row }) => money(num(row.original.parts_total) || num(row.original.parts)),
        };
      case 'labor_hours':
        return {
          accessorKey: 'labor_hours',
          header: label,
          size: 60,
          enableEditing: true,
          accessorFn: row => row.labor_hours,
          muiEditTextFieldProps: ({ row }) => ({
            disabled: editDisabled(row),
            type: 'number',
            onBlur: e => patch(row.original.id, { labor_hours: num(e.target.value), ...recalc(row.original, { labor_hours: e.target.value }) }),
          }),
          Cell: ({ row }) => row.original.labor_hours ?? 0,
        };
      case 'labor_rate':
        return {
          accessorKey: 'labor_rate',
          header: label,
          size: 60,
          enableEditing: true,
          accessorFn: row => row.labor_rate,
          muiEditTextFieldProps: ({ row }) => ({
            disabled: editDisabled(row),
            type: 'number',
            onBlur: e => patch(row.original.id, { labor_rate: num(e.target.value), ...recalc(row.original, { labor_rate: e.target.value }) }),
          }),
          Cell: ({ row }) => money(row.original.labor_rate),
        };
      case 'labor_total':
        return {
          accessorKey: 'labor_total',
          header: label,
          size: 60,
          enableEditing: false,
          accessorFn: row => (num(row.labor_hours) * num(row.labor_rate)).toFixed(2),
          Cell: ({ row }) => money(num(row.original.labor_hours) * num(row.original.labor_rate)),
        };
      case 'estimate_total':
        return {
          accessorKey: 'estimate_total',
          header: label,
          size: 70,
          enableEditing: true,
          accessorFn: row => row.estimate_total,
          muiEditTextFieldProps: ({ row }) => ({
            disabled: editDisabled(row),
            type: 'number',
            onBlur: e => patch(row.original.id, { estimate_total: num(e.target.value) }),
          }),
          Cell: ({ row }) => {
            const calc = num(row.original.parts_total) + num(row.original.labor_hours) * num(row.original.labor_rate);
            const stored = num(row.original.estimate_total);
            return money(stored > 0 ? stored : calc);
          },
        };
      case 'ro_status':
        return {
          accessorKey: 'ro_status',
          header: label,
          enableEditing: false,
          size: 220,
          accessorFn: row => row.ro_status,
          Cell: ({ row }) => statusCell(row.original),
        };
      case 'quantity':
        return {
          accessorKey: 'quantity',
          header: label,
          size: 60,
          enableEditing: true,
          accessorFn: row => row.quantity,
          muiEditTextFieldProps: ({ row }) => ({
            disabled: editDisabled(row),
            type: 'number',
            onBlur: e => patch(row.original.id, { quantity: num(e.target.value), ...recalc(row.original, { quantity: e.target.value }) }),
          }),
          Cell: ({ row }) => (row.original.quantity ?? 1),
        };
      default:
        if (inputtype === 'checkbox') {
          return {
            accessorKey: field,
            header: label,
            enableEditing: false,
            size: 90,
            Cell: ({ row }) => (
              <Checkbox
                checked={isFlagged(row.original[field])}
                disabled={editDisabled(row)}
                onChange={e => patch(row.original.id, { [field]: e.target.checked })}
              />
            ),
          };
        }
        if (inputtype === 'text' || inputtype === 'textarea' || !inputtype) {
          return {
            accessorKey: field,
            header: label,
            size: 140,
            enableEditing: true,
            accessorFn: row => row[field],
            muiEditTextFieldProps: ({ row }) => ({
              disabled: editDisabled(row),
              multiline: inputtype === 'textarea',
              onBlur: e => patch(row.original.id, { [field]: e.target.value }),
            }),
            Cell: ({ row }) => (row.original[field] != null ? String(row.original[field]) : ''),
          };
        }
        return {
          accessorKey: field,
          header: label,
          enableEditing: false,
          size: 120,
          accessorFn: row => row[field],
          Cell: ({ row }) => (row.original[field] != null ? String(row.original[field]) : ''),
        };
    }
  };

  const headerConfig = useMemo(() => {
    const byWorkItems = formInstructions?.headers?.work_items;
    const keys = Object.keys(formInstructions?.headers || {});
    const fallbackSection = keys.length ? formInstructions.headers[keys[0]] : null;
    const cfg = (byWorkItems && byWorkItems.length) ? byWorkItems : fallbackSection;
    return (cfg && cfg.length) ? cfg : DEFAULT_HEADERS;
  }, [formInstructions]);

  const columns = useMemo(() => {
    const built = headerConfig.map(h => buildColumn(h)).filter(Boolean);
    built.push({
      id: 'workitem_actions',
      header: '',
      enableEditing: false,
      size: 90,
      Cell: ({ row }) => (APPROVED_STATES.includes(row.original.ro_status) ? null : (
        <button className="btn btn-sm btn-outline-danger" onClick={removeFromWorkItems(row.original)} disabled={isDisabled}>
          Remove
        </button>
      )),
    });
    return built;
  }, [headerConfig, activeVehicle, isDisabled, canUndo, cantApprove, uid, workItemOptions]);

  const tableTheme = useMemo(() => createTheme({ palette: { mode: 'light' } }), []);

  if (!activeVehicle) return null;

  return (
    <ThemeProvider theme={tableTheme}>
      <div className="container-fluid pad-left-2 pad-right-2">
        <VehiclePartsSection vehicle={activeVehicle} tab={tab} />
        <MaterialReactTable
          columns={columns}
          data={rows}
          enableEditing
          editDisplayMode="cell"
          enableRowSelection={row => row.original.ro_status === 'pending' && !isDisabled}
          getRowId={row => String(row.id)}
          initialState={{
            density: 'compact',
            pagination: { pageIndex: 0, pageSize: 25 },
            columnVisibility: { labor_rate: false },
          }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box sx={{
              display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'flex-start',
            }}
            >
              <Box sx={{
                display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap',
              }}
              >
                <Button variant="contained" onClick={addLine} disabled={isDisabled}>Add Work Item</Button>
                <Button variant="contained" onClick={() => setFlyoutOpen(true)} disabled={isDisabled}>
                  Add Multiple Work Items
                </Button>
                <Button variant="outlined" onClick={() => setPullOpen(true)} disabled={isDisabled || pullCandidates.length === 0}>
                  Pull From Inspection
                </Button>
                {confirmingTask ? (
                  <div className="btn-group" role="group">
                    <button className="btn btn-sm btn-success" onClick={() => runBulk(confirmingTask, table)}>
                      Confirm {confirmingTask} Selected
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => setConfirmingTask(null)}>Back</button>
                  </div>
                ) : (
                  <div className="btn-group" role="group">
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => setConfirmingTask('Approve')}
                      disabled={table.getSelectedRowModel().rows.length === 0 || cantApprove}
                    >
                      Approve Selected
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => setConfirmingTask('Deny')}
                      disabled={table.getSelectedRowModel().rows.length === 0 || cantApprove}
                    >
                      Deny Selected
                    </button>
                  </div>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <span>Parts Total: ${money(totals.parts)}</span>
                <span>Labor Total: ${money(totals.labor)}</span>
                <span>Total: ${money(totals.total)}</span>
                <span>Approved Total: ${money(totals.approved)}</span>
              </Box>
            </Box>
          )}
          renderDetailPanel={({ row }) => (
            <Box>
              {workItemPartsEnabled && (
                <PartsSection mpiWorkItem={row.original} vehicleId={activeVehicle.id} tab={tab} />
              )}
              <MpiWorkItemAttachments mpiWorkItemId={row.original.id} vehicleId={activeVehicle.id} />
            </Box>
          )}
        />
        <Dialog open={flyoutOpen} onClose={() => setFlyoutOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Select Work Items</DialogTitle>
          <DialogContent sx={{ padding: 1 }}>
            <Box sx={{ mb: 2, display: 'flex', gap: '1rem' }}>
              {['all', 'mechanical', 'cosmetic'].map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'contained' : 'outlined'}
                  onClick={() => setSelectedCategory(cat)}
                  className="text-capitalize"
                >
                  {cat}
                </Button>
              ))}
            </Box>
            <Grid
              container
              spacing={1}
              sx={{ maxHeight: '60vh', overflowY: 'auto', justifyContent: 'space-between' }}
            >
              {presetData.map((item, index) => {
                const alreadyShown = rows.some(r => r.name === item.name);
                return (
                  <Grid key={item.id} size={3}>
                    <Box
                      sx={{
                        padding: '4px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        backgroundColor: selectedPresets[index] ? '#e3f2fd' : (alreadyShown ? '#e0e0e0' : 'white'),
                        textAlign: 'center',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectedPresets(prev => ({ ...prev, [index]: !prev[index] }))}
                    >
                      {item.name}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setSelectedPresets({}); setFlyoutOpen(false); }} color="error">Cancel</Button>
            <Button onClick={onClickMultiple} color="success" disabled={Object.values(selectedPresets).every(v => !v)}>
              Create Selected Work Items
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={pullOpen} onClose={() => setPullOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Pull Inspection Items Into Work Items</DialogTitle>
          <DialogContent dividers>
            {pullCandidates.length === 0 ? (
              <Box sx={{ py: 2, color: 'text.secondary' }}>No additional inspection items to pull.</Box>
            ) : pullCandidates.map(item => (
              <FormControlLabel
                key={item.id}
                sx={{ display: 'flex' }}
                control={(
                  <Checkbox
                    checked={!!pullSelected[item.id]}
                    onChange={() => setPullSelected(p => ({ ...p, [item.id]: !p[item.id] }))}
                  />
                )}
                label={`${item.name || '(no name)'}${item.category ? ` — ${item.category}` : ''}`}
              />
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setPullSelected({}); setPullOpen(false); }} color="error">Cancel</Button>
            <Button onClick={confirmPull} color="success" disabled={Object.values(pullSelected).every(v => !v)}>Add Selected</Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  );
};

export default WorkItemsForm;
